import { useCallback, useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { PurposePanel, ServicesPanel, WhoPanel, ContactPanel } from "./SheetPanels";
import { useHeroLine } from "./useHeroLine";

const SECTIONS = [
  { id: "purpose", num: "01", label: "Purpose", C: PurposePanel },
  { id: "what-we-do", num: "02", label: "What we do", C: ServicesPanel },
  { id: "who", num: "03", label: "Who you deal with", C: WhoPanel },
  { id: "contact", num: "04", label: "Contact", C: ContactPanel },
];
const N = SECTIONS.length;

const initialIndex = () => {
  const i = SECTIONS.findIndex((s) => s.id === window.location.hash.slice(1));
  return i === -1 ? 0 : i;
};

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const NAV_EASE = "elastic.out(1,0.6)";  // nav progress elastic snap
const TOTALS = [18.6, 34.8, 54.2, 69.0]; // running frontage total per section
const NAV_PARALLAX = -0.85; // heading layer rate vs the camera (<0 trails, >0 leads)

// The site is a single camera panning across a fixed strip of four panels. This
// component owns navigation (the camera, the tab index, wheel/touch/keys, the
// progress readout); the hero's falling line and its reveal live in useHeroLine,
// which rides the same camera so the finished hero pans with the content.
export const Sheets = () => {
  const [active, setActive] = useState(initialIndex);
  const activeRef = useRef(active);
  const animatingRef = useRef(false);
  const lockRef = useRef(false);
  const touchY = useRef(null);

  const areaRef = useRef(null);
  const stripRef = useRef(null);
  const fillRef = useRef(null);
  const readRef = useRef(null);
  const meas = useRef({ v: 0 });
  const panelRefs = useRef([]);
  const cam = useRef({ w: 0 });   // camera offset, shared with the hero line
  const camTl = useRef(null);

  // the running-total readout + progress fill for a section; fires when the hero
  // line reports ready so it stays in step with the intro
  const showNav = useCallback((idx, animate = true) => {
    const pct = `${((idx + 1) / N) * 100}%`;
    if (fillRef.current) {
      if (animate && !prefersReduced()) gsap.fromTo(fillRef.current, { width: "0%" }, { width: pct, duration: 1.4, delay: 1.9, ease: NAV_EASE });
      else fillRef.current.style.width = pct;
    }
    if (readRef.current) {
      const total = TOTALS[idx];
      if (animate && !prefersReduced()) { meas.current.v = 0; gsap.to(meas.current, { v: total, duration: 0.9, delay: 1.9, ease: "power2.out", onUpdate: () => { if (readRef.current) readRef.current.textContent = meas.current.v.toFixed(1) + "m"; } }); }
      else readRef.current.textContent = total.toFixed(1) + "m";
    }
  }, []);

  const hero = useHeroLine({ areaRef, stripRef, panelRefs, camRef: cam, activeRef, onReady: showNav });

  const travel = (to) => {
    const from = activeRef.current;
    if (animatingRef.current || to === from || to < 0 || to > N - 1) return;
    // the hero line settles to its finished resting state and simply pans with the
    // camera, rather than fading out and re-dropping
    hero.setInteractive(false);
    hero.settleRest();
    const vw = areaRef.current ? areaRef.current.clientWidth : 0;
    activeRef.current = to;
    setActive(to);
    window.history.replaceState(null, "", `#${SECTIONS[to].id}`);
    const pct = `${((to + 1) / N) * 100}%`;

    if (prefersReduced()) {
      cam.current.w = to * vw;
      hero.setInteractive(to === 0);
      if (fillRef.current) fillRef.current.style.width = pct;
      if (readRef.current) readRef.current.textContent = TOTALS[to].toFixed(1) + "m";
      hero.draw();
      return;
    }

    animatingRef.current = true;
    lockRef.current = true;
    const dist = Math.abs(to - from);
    const Dc = 0.6 + 0.14 * dist;
    if (camTl.current) camTl.current.kill();
    camTl.current = gsap.to(cam.current, {
      w: to * vw,
      duration: Dc,
      ease: "power4.out",
      onComplete: () => { animatingRef.current = false; lockRef.current = false; cam.current.w = to * vw; hero.setInteractive(to === 0); },
    });
    if (fillRef.current) gsap.to(fillRef.current, { width: pct, duration: 0.75, ease: NAV_EASE });
    if (readRef.current) gsap.to(meas.current, { v: TOTALS[to], duration: 0.7, ease: "power2.out", onUpdate: () => { if (readRef.current) readRef.current.textContent = meas.current.v.toFixed(1) + "m"; } });
  };

  const step = (d) => { if (!lockRef.current) travel(activeRef.current + d); };

  const innerScrollBlocks = (dir) => {
    const el = panelRefs.current[activeRef.current];
    if (!el || el.scrollHeight <= el.clientHeight + 4) return false;
    if (dir > 0) return el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    return el.scrollTop > 4;
  };

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (["ArrowDown", "ArrowRight", "PageDown", " "].includes(e.key)) { e.preventDefault(); step(1); }
      if (["ArrowUp", "ArrowLeft", "PageUp"].includes(e.key)) { e.preventDefault(); step(-1); }
    };
    const onGoto = (e) => travel(e.detail);
    window.addEventListener("keydown", onKey);
    window.addEventListener("sheet-goto", onGoto);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("sheet-goto", onGoto);
      if (camTl.current) camTl.current.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // headings ride a parallax layer: translate anything tagged [data-nav-heading]
  // relative to the camera so it moves at a different rate than the copy as a
  // section slides in (K > 0 leads, K < 0 trails), with a motion blur that scales
  // with the heading's per-frame speed and clears when it settles.
  useEffect(() => {
    if (prefersReduced()) return;
    const K = NAV_PARALLAX, prev = [];
    let prevHero;
    const motionBlur = (el, vel) => {
      const b = Math.min(6, vel * 0.12);
      el.style.filter = b > 0.4 ? `blur(${b.toFixed(1)}px)` : "";
    };
    const paint = () => {
      const vw = areaRef.current ? areaRef.current.clientWidth : 0;
      if (!vw) return;
      for (let i = 0; i < panelRefs.current.length; i++) {
        const p = panelRefs.current[i];
        const h = p && p.querySelector("[data-nav-heading]");
        if (!h) continue;
        const x = K * (cam.current.w - i * vw);
        prev[i] = prev[i] === undefined ? x : prev[i];
        motionBlur(h, Math.abs(x - prev[i]));
        prev[i] = x;
        h.style.transform = `translate3d(${x.toFixed(2)}px,0,0)`;
      }
      // the hero rides the same parallax + motion blur. The restructure put the
      // headline (with its peel clip) and the dimension line in one [data-hero-unit],
      // so moving or blurring the whole unit carries both together and the clip stays
      // aligned — the thing that used to break when the headline moved on its own. At
      // home the offset is 0 and the transform is cleared, so the interactive peel
      // composites clean with no leftover layer.
      const p0 = panelRefs.current[0];
      const hero = p0 && p0.querySelector("[data-hero-unit]");
      if (hero) {
        const xh = K * cam.current.w;
        prevHero = prevHero === undefined ? xh : prevHero;
        motionBlur(hero, Math.abs(xh - prevHero));
        prevHero = xh;
        hero.style.transform = Math.abs(xh) < 0.01 ? "" : `translate3d(${xh.toFixed(2)}px,0,0)`;
      }
    };
    gsap.ticker.add(paint);
    return () => gsap.ticker.remove(paint);
  }, []);

  const onWheel = (e) => {
    if (lockRef.current) return;
    const mag = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (Math.abs(mag) < 25) return;
    const dir = mag > 0 ? 1 : -1;
    if (innerScrollBlocks(dir)) return;
    step(dir);
  };
  const onTouchStart = (e) => { touchY.current = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    if (touchY.current === null || lockRef.current) return;
    const delta = touchY.current - e.changedTouches[0].clientY;
    touchY.current = null;
    if (Math.abs(delta) < 60) return;
    const dir = delta > 0 ? 1 : -1;
    if (innerScrollBlocks(dir)) return;
    step(dir);
  };

  return (
    <div
      className="fixed inset-0 pt-14 flex flex-col bg-[#f9f9f7]"
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="sheets-container"
    >
      <div ref={areaRef} className="relative flex-1 overflow-hidden">
        <div ref={stripRef} className="absolute inset-0 flex will-change-transform" style={{ width: `${N * 100}%` }}>
          {SECTIONS.map((s, i) => (
            <div
              key={s.id}
              ref={(el) => (panelRefs.current[i] = el)}
              className="h-full overflow-y-auto overflow-x-hidden"
              style={{ width: `${100 / N}%` }}
              aria-hidden={i !== active}
            >
              <s.C />
            </div>
          ))}
        </div>
      </div>

      <nav className="border-t rule shrink-0" aria-label="Site sections" data-testid="sheet-index">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12">
          <div className="relative">
            <div className="flex">
              {SECTIONS.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => travel(i)}
                  className="flex-1 text-left pt-3 pb-7 group"
                  data-testid={`sheet-tab-${s.id}`}
                  aria-current={i === active ? "page" : undefined}
                >
                  <span className={`label transition-colors duration-300 ${i === active ? "text-[#ff4f00]" : "text-[#999994] group-hover:text-[#111111]"}`}>
                    {s.num}
                    <span className="hidden md:inline ml-2">{s.label}</span>
                  </span>
                </button>
              ))}
            </div>
            <div className="absolute bottom-3 left-0 right-0 h-3 pointer-events-none">
              <div className="absolute inset-x-0 top-1/2 h-px bg-[#e5e5e0]" />
              {SECTIONS.map((_, i) => (
                <span key={i} className="absolute top-1/2 -translate-y-1/2 w-px h-2 bg-[#d8d8d2]" style={{ left: `${(i / N) * 100}%` }} />
              ))}
              <div ref={fillRef} className="absolute left-0 top-0 h-3 flex items-center" style={{ width: 0 }}>
                <span className="block w-[2px] h-3 bg-[#ff4f00] shrink-0" />
                <span className="block h-[2px] flex-1 bg-[#ff4f00]" />
                <span className="block w-[2px] h-3 bg-[#ff4f00] shrink-0" />
                <span ref={readRef} aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 top-[9px] whitespace-nowrap" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "11px", letterSpacing: "0.03em", color: "#ff4f00" }}>0.0m</span>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};
